import React from 'react';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const NotFound = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { lang } = useParams();
  
  // Function to generate language-aware links
  const getLanguagePath = (path) => {
    const currentLang = lang || 'en';
    return `/${currentLang}${path}`;
  };
  
  return (
    <Box
      sx={{
        py: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: '70vh',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: theme.palette.mode === 'dark' 
            ? 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.05), transparent 70%)' 
            : 'radial-gradient(circle at top right, rgba(22, 56, 50, 0.05), transparent 70%)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <ErrorOutlineIcon 
          sx={{ 
            fontSize: '8rem', 
            color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
            mb: 3,
            opacity: 0.8
          }} 
        />
        
        <Typography 
          variant="h1" 
          sx={{ 
            mb: 2,
            fontWeight: 800,
            fontSize: { xs: '3rem', md: '5rem' },
            backgroundImage: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, #fff 0%, #B0BEC5 100%)'
              : 'linear-gradient(90deg, #2C3E50 0%, #163832 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            ...(theme.palette.mode === 'dark' && { WebkitTextFillColor: 'transparent' })
          }}
        >
          404
        </Typography>
        
        <Typography 
          variant="h3" 
          sx={{ 
            mb: 4,
            fontWeight: 700
          }}
        >
          {t('notFound.title')}
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 6,
            fontSize: '1.2rem',
            maxWidth: '700px',
            mx: 'auto'
          }}
        >
          {t('notFound.message')}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large" 
            component={Link}
            to={getLanguagePath('/')}
            sx={{ 
              fontWeight: 700,
              py: 1.5, 
              px: 4
            }}
          >
            {t('notFound.backHome')}
          </Button>
          
          <Button 
            variant="outlined" 
            color="primary" 
            size="large" 
            component={Link}
            to={getLanguagePath('/contact')}
            sx={{ 
              fontWeight: 700,
              py: 1.5, 
              px: 4,
              borderColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
              color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : theme.palette.primary.dark,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(22, 56, 50, 0.04)',
              }
            }}
          >
            {t('contact.title')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFound; 